import cx from 'classnames';
import { parseISO } from 'date-fns';
import { useParams } from 'react-router-dom';
import { ButtonVariants } from '@components/button/Button';
import { Icon } from '@components/icon/Icon';
import { ImageGalleryPreview } from '@components/imageGallery/ImageGalleryPreview';
import { LinkStyledAsButton } from '@components/linkStyledAsButton/LinkStyledAsButton';
import { LoadingPage } from '@components/LoadingPage';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { type RestaurantEvent } from '@shared/types/restaurants';
import { centsToDecimal } from '@shared/utils/currency';
import { toLongWeekdayMonthDateAndYear } from '@shared/utils/dateFormatters';
import { ISOTimeTo12HourTime } from '@shared/utils/time';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { NotFoundPage } from 'webReservations/errors/NotFound/NotFoundPage';
import { useAvailableDates } from 'webReservations/hooks/useAvailableDates';
import { Layout } from 'webReservations/layout/Layout';
import typography from '~styles/typography.scss';
import { getRestaurantEventById, type RestaurantDetails } from './apiHelpers';
import styles from './RestaurantEventPage.scss';

export const RestaurantEventPage = () => {
  const { restaurantDetails: restaurant } = useRestaurantContext();
  const { availableDates } = useAvailableDates();
  const { eventId } = useParams();

  const {
    data: restaurantEvent,
    isPending,
    error,
  } = useAbortEffect(
    {
      effect: async () => getRestaurantEventById(restaurant.id, eventId!),
    },
    [restaurant.id, eventId],
  );

  if (isPending) {
    return <LoadingPage />;
  }

  if (!restaurantEvent || error) {
    return <NotFoundPage />;
  }

  const minGuestsForEvent =
    availableDates[restaurantEvent.shift.startDate]?.minGuests || 2;

  const eventPathParams = new URLSearchParams({
    date: restaurantEvent.shift.startDate,
    guestCount: minGuestsForEvent.toString(),
  });
  const eventPath = `/${restaurant.locationSlug}/${restaurant.nameSlug}?${eventPathParams.toString()}`;

  return (
    <Layout hideBorder>
      <RestaurantEventDetailsPageHeader
        event={restaurantEvent}
        restaurant={restaurant}
      />
      <div className={cx(styles.pageMargin, styles.pageContent)}>
        <div className={styles.container}>
          <section className={styles.photoSection}>
            <h2 className={typography.h6}>Photo Gallery</h2>
            <ImageGalleryPreview
              eventName={restaurantEvent.name}
              images={restaurantEvent.images}
            />
          </section>
          <section className={styles.descriptionSection}>
            <h2 className={cx(typography.h6, styles.descriptionLabel)}>
              Event Description
            </h2>
            <p className={cx(styles.description)}>
              {restaurantEvent.description}
            </p>
          </section>
          <aside className={styles.sidebar}>
            <div className={styles.eventDetailsCard}>
              <div className={styles.detailItem}>
                <h2 className={typography.h6}>Event Details</h2>
              </div>

              <div className={styles.detailItem}>
                <Icon name="calendar" className={styles.detailIcon} />
                <div>
                  <p className={cx(styles.detailValue)}>
                    {toLongWeekdayMonthDateAndYear(
                      parseISO(restaurantEvent.shift.startDate),
                    )}
                  </p>
                </div>
              </div>

              <div className={styles.detailItem}>
                <Icon name="clock" className={styles.detailIcon} />
                <div>
                  <p className={styles.detailValue}>
                    {formatTime(restaurantEvent)}
                  </p>
                </div>
              </div>

              <div className={styles.detailItem}>
                <Icon name="locationArrow" className={styles.detailIcon} />
                <div>
                  <p className={styles.detailValue}>
                    <span>{restaurant.name}</span>
                    <br />
                    <span>{formatAddress(restaurant)}</span>
                  </p>
                </div>
              </div>

              <div className={styles.detailItem}>
                <Icon name="creditCard" className={styles.detailIcon} />
                <div>
                  <p className={cx(styles.detailValue)}>
                    {`$${centsToDecimal(restaurantEvent.price)}`}
                  </p>
                </div>
              </div>
              <div className={styles.detailItem}>
                <LinkStyledAsButton
                  variant={ButtonVariants.Primary}
                  label="Choose Seat"
                  to={eventPath}
                  reloadDocument
                />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
};

const RestaurantEventDetailsPageHeader = ({
  event,
  restaurant,
}: {
  event: RestaurantEvent;
  restaurant: RestaurantDetails;
}) => (
  <header className={styles.pageHeader}>
    <div className={styles.pageMargin}>
      <div className={styles.headerContent}>
        <div className={styles.headerLeft}>
          <div className={styles.eventEyebrow}>
            <Icon
              name="eventCalendar"
              strokeWidth={0.2}
              className={styles.eventIcon}
            />
            <span className={typography.t1}>Event</span>
          </div>

          <h1 className={cx(typography.d3, styles.eventTitle)}>{event.name}</h1>
          <p className={styles.eventSubtitle}>
            <span
              className={cx(typography.c3, styles.eventSubtitleRestaurantName)}
            >
              {restaurant.name} -&nbsp;
            </span>
            <span className={typography.c3}>{formatAddress(restaurant)}</span>
          </p>
        </div>
      </div>
    </div>
  </header>
);

function formatAddress(restaurant: RestaurantDetails): string {
  return `${restaurant.street}, ${restaurant.city}, ${restaurant.state} ${restaurant.postalCode}`;
}

function formatTime(restaurantEvent: RestaurantEvent): string {
  return `${ISOTimeTo12HourTime(restaurantEvent.shift.startTime)} - ${ISOTimeTo12HourTime(restaurantEvent.shift.endTime)}`;
}
